import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Phone, Mail, School, BookOpen, TrendingUp, Flame, Award, Calendar, Clock, CheckCircle, Video, Target, ArrowLeft, Zap, User, MapPin, Users, Briefcase, GraduationCap, Cake, FileText, Lock, Unlock, Save } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function StudentProfileView({ userId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userData, setUserData] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [fieldLocks, setFieldLocks] = useState({});
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState([]);
  const [courseProgress, setCourseProgress] = useState([]);
  const [performanceStats, setPerformanceStats] = useState({
    lessonsCompleted: 0,
    totalLessons: 0,
    quizzesCompleted: 0,
    totalQuizzes: 0,
    quizAverage: 0,
    totalLearningTime: 0,
    overallProgress: 0
  });

  useEffect(() => {
    if (userId) {
      fetchAllData();
    }
  }, [userId]);

  const fetchAllData = async () => {
    try {
      await Promise.all([
        fetchCustomFieldDefinitions(),
        fetchUserData(),
        fetchCourseProgress(),
        fetchPerformanceStats()
      ]);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load student profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomFieldDefinitions = async () => {
    try {
      const res = await axios.get(`${API}/custom-fields?include_hidden=true`, { headers: getAuthHeaders() });
      setCustomFieldDefinitions(res.data);
    } catch (error) {
      console.error('Failed to fetch custom field definitions:', error);
    }
  };

  const fetchUserData = async () => {
    try {
      const res = await axios.get(`${API}/users/${userId}`, { headers: getAuthHeaders() });
      setUserData(res.data);
      setEditedData(res.data);
      
      // Fetch field locks
      try {
        const locksRes = await axios.get(`${API}/admin/users/${userId}/field-locks`, { headers: getAuthHeaders() });
        setFieldLocks(locksRes.data.field_locks || {});
      } catch (error) {
        console.error('Field locks not available:', error);
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
    }
  };

  const fetchCourseProgress = async () => {
    try {
      const coursesRes = await axios.get(`${API}/courses`, { headers: getAuthHeaders() });
      const userCourses = coursesRes.data.filter(course => 
        course.students_assigned && course.students_assigned.includes(userId)
      );
      
      const progressPromises = userCourses.map(async (course) => {
        try {
          const progressRes = await axios.get(`${API}/admin/progress/course/${course.id}/user/${userId}`, { headers: getAuthHeaders() });
          return {
            courseId: course.id,
            courseName: course.title,
            progress: progressRes.data.completion_percentage || progressRes.data.progress_percentage || 0,
            lessonsCompleted: progressRes.data.lessons_completed || 0,
            totalLessons: progressRes.data.total_lessons || 0
          };
        } catch (error) {
          return {
            courseId: course.id,
            courseName: course.title,
            progress: 0,
            lessonsCompleted: 0,
            totalLessons: 0
          };
        }
      });

      const results = await Promise.all(progressPromises);
      setCourseProgress(results);
    } catch (error) {
      console.error('Failed to fetch course progress:', error);
    }
  };

  const fetchPerformanceStats = async () => {
    try {
      const res = await axios.get(`${API}/users/${userId}/performance`, { headers: getAuthHeaders() });
      setPerformanceStats(res.data);
    } catch (error) {
      console.error('Failed to fetch performance stats:', error);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCustomFieldChange = (fieldId, value) => {
    console.log('Custom field change:', fieldId, value); // Debug log
    setEditedData(prev => ({
      ...prev,
      custom_fields: {
        ...prev.custom_fields,
        [fieldId]: {
          value: value,
          updated_at: new Date().toISOString(),
          updated_by: prev.id || 'admin'
        }
      }
    }));
  };

  const toggleFieldLock = (fieldName) => {
    setFieldLocks(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      
      // Save profile data
      await axios.put(`${API}/admin/users/${userId}`, editedData, { headers: getAuthHeaders() });
      
      // Save field locks
      await axios.post(`${API}/admin/users/${userId}/field-locks`, 
        { field_locks: fieldLocks },
        { headers: getAuthHeaders() }
      );
      
      toast.success('Profile saved successfully!');
      
      // Refresh data
      await fetchUserData();
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const getCustomFieldValue = (label) => {
    const field = customFieldDefinitions.find(f => f.label === label);
    if (!field) return '';
    const fieldData = editedData.custom_fields?.[field.id];
    // Handle both direct values and nested value objects
    if (typeof fieldData === 'object' && fieldData !== null && 'value' in fieldData) {
      return fieldData.value || '';
    }
    return fieldData || '';
  };

  // Helper to get fees values from either custom fields or direct user data
  const getFeesPaid = () => {
    const customValue = getCustomFieldValue('Fees Paid');
    if (customValue) return parseFloat(customValue) || 0;
    return parseFloat(editedData.fees_paid) || 0;
  };

  const getFeesRemaining = () => {
    const customValue = getCustomFieldValue('Fees Remaining');
    if (customValue) return parseFloat(customValue) || 0;
    return parseFloat(editedData.fees_remaining) || 0;
  };

  // Calculate fees classification
  const getFeesClassification = () => {
    const remaining = getFeesRemaining();
    const paid = getFeesPaid();
    
    if (paid === 0 && remaining === 0) {
      return { status: 'Not Set', color: 'bg-gray-100 text-gray-600 border-gray-300' };
    }
    if (remaining === 0 && paid > 0) {
      return { status: 'Full Fees Paid', color: 'bg-green-100 text-green-700 border-green-300' };
    }
    if (remaining > 0) {
      return { status: 'Partial Fees Paid', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' };
    }
    return { status: 'Not Set', color: 'bg-gray-100 text-gray-600 border-gray-300' };
  };

  const EditableField = ({ label, value, onChange, fieldName, icon: Icon, type = "text", options = null }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2 text-sm font-semibold text-navy">
          {Icon && <Icon className="w-4 h-4 text-primary-blue" />}
          <span>{label}</span>
        </Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => toggleFieldLock(fieldName)}
          className="h-8 w-8 p-0"
          title={fieldLocks[fieldName] ? 'Unlock field for student' : 'Lock field from student'}
        >
          {fieldLocks[fieldName] ? 
            <Lock className="w-4 h-4 text-red-500" /> : 
            <Unlock className="w-4 h-4 text-green-500" />
          }
        </Button>
      </div>
      {options ? (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className="h-10 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue">
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent className="bg-blue-50 border-2 border-blue-200">
            {options.map(option => (
              <SelectItem key={option} value={option}>{option}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Input
          type={type}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue"
        />
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#4D4D4D]">Loading student profile...</p>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="p-8 text-center">
        <p className="text-[#4D4D4D]">Student not found</p>
        <Button onClick={onClose} className="mt-4">Go Back</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F7F7F7] to-[#E7E7E7] p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex items-center gap-2 rounded-xl"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <h1 className="text-2xl md:text-3xl font-bold text-navy">Edit Student Profile</h1>
          <div className="w-24"></div>
        </div>

        {/* Student Header Card - FIXED: Solid blue background */}
        <Card className="border-0 card-shadow bg-gradient-to-br from-[#1E88E5] to-[#42A5F5] text-white rounded-2xl overflow-hidden">
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center text-4xl font-bold text-primary-blue shadow-lg">
                {userData.first_name?.[0]}{userData.last_name?.[0]}
              </div>
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-3xl font-bold mb-2">
                  {userData.first_name} {userData.last_name}
                </h2>
                <p className="text-white/95 mb-3 text-lg">{userData.email}</p>
                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                  <Badge className="bg-white text-primary-blue border-0 font-semibold">
                    {userData.role?.toUpperCase()}
                  </Badge>
                  {userData.profile_verified && (
                    <Badge className="bg-green-500 text-white border-0 font-semibold">
                      ✓ Verified
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Complete Profile Details - IMPROVED: Better UI consistency */}
        <Card className="border-0 card-shadow bg-white rounded-2xl">
          <CardHeader className="border-b border-gray-100 pb-4">
            <CardTitle className="text-xl font-bold text-navy flex items-center gap-2">
              <User className="w-5 h-5 text-primary-blue" />
              Complete Profile Details
            </CardTitle>
            <p className="text-sm text-[#4D4D4D] mt-2 flex items-center gap-2">
              <span className="inline-flex items-center gap-1">🔒 = Locked for student</span>
              <span className="text-gray-400">|</span>
              <span className="inline-flex items-center gap-1">🔓 = Editable by student</span>
            </p>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Row 1: First Name, Father's Name */}
              <EditableField
                label="First Name"
                value={editedData.first_name}
                onChange={(val) => handleFieldChange('first_name', val)}
                fieldName="first_name"
                icon={User}
              />
              <EditableField
                label="Father's Name"
                value={getCustomFieldValue('Father\'s Name') || getCustomFieldValue('Father Name')}
                onChange={(val) => {
                  const field = customFieldDefinitions.find(f => f.label === "Father's Name" || f.label === "Father Name");
                  if (field) handleCustomFieldChange(field.id, val);
                }}
                fieldName="father_name"
                icon={Users}
              />
              
              {/* Row 2: Mother's Name, Last Name */}
              <EditableField
                label="Mother's Name"
                value={getCustomFieldValue('Mother\'s Name') || getCustomFieldValue('Mother Name')}
                onChange={(val) => {
                  const field = customFieldDefinitions.find(f => f.label === "Mother's Name" || f.label === "Mother Name");
                  if (field) handleCustomFieldChange(field.id, val);
                }}
                fieldName="mother_name"
                icon={Users}
              />
              <EditableField
                label="Last Name"
                value={editedData.last_name}
                onChange={(val) => handleFieldChange('last_name', val)}
                fieldName="last_name"
                icon={User}
              />
              
              {/* Row 3: Father's Mobile Number, Mother's Mobile Number */}
              <EditableField
                label="Father's Mobile Number"
                value={getCustomFieldValue('Father\'s Mobile Number') || getCustomFieldValue('Father Mobile') || getCustomFieldValue('Father\'s Mobile')}
                onChange={(val) => {
                  const field = customFieldDefinitions.find(f => f.label === "Father's Mobile Number" || f.label === "Father Mobile" || f.label === "Father's Mobile");
                  if (field) handleCustomFieldChange(field.id, val);
                }}
                fieldName="father_mobile"
                icon={Phone}
                type="tel"
              />
              <EditableField
                label="Mother's Mobile Number"
                value={getCustomFieldValue('Mother\'s Mobile Number') || getCustomFieldValue('Mother Mobile') || getCustomFieldValue('Mother\'s Mobile')}
                onChange={(val) => {
                  const field = customFieldDefinitions.find(f => f.label === "Mother's Mobile Number" || f.label === "Mother Mobile" || f.label === "Mother's Mobile");
                  if (field) handleCustomFieldChange(field.id, val);
                }}
                fieldName="mother_mobile"
                icon={Phone}
                type="tel"
              />
              
              {/* Row 4: Father's Occupation, Mother's Occupation */}
              <EditableField
                label="Father's Occupation"
                value={getCustomFieldValue('Father\'s Occupation') || getCustomFieldValue('Father Occupation')}
                onChange={(val) => {
                  const field = customFieldDefinitions.find(f => f.label === "Father's Occupation" || f.label === "Father Occupation");
                  if (field) handleCustomFieldChange(field.id, val);
                }}
                fieldName="father_occupation"
                icon={Briefcase}
              />
              <EditableField
                label="Mother's Occupation"
                value={getCustomFieldValue('Mother\'s Occupation') || getCustomFieldValue('Mother Occupation')}
                onChange={(val) => {
                  const field = customFieldDefinitions.find(f => f.label === "Mother's Occupation" || f.label === "Mother Occupation");
                  if (field) handleCustomFieldChange(field.id, val);
                }}
                fieldName="mother_occupation"
                icon={Briefcase}
              />
              
              {/* Row 5: Your Mobile Number, School Name */}
              <EditableField
                label="Your Mobile Number"
                value={editedData.mobile_number}
                onChange={(val) => handleFieldChange('mobile_number', val)}
                fieldName="mobile_number"
                icon={Phone}
                type="tel"
              />
              <EditableField
                label="School Name"
                value={editedData.school_name}
                onChange={(val) => handleFieldChange('school_name', val)}
                fieldName="school_name"
                icon={School}
              />
              
              {/* Row 6: School Board, Grade & Year */}
              <EditableField
                label="School Board"
                value={getCustomFieldValue('School Board')}
                onChange={(val) => {
                  const field = customFieldDefinitions.find(f => f.label === 'School Board');
                  if (field) handleCustomFieldChange(field.id, val);
                }}
                fieldName="school_board"
                icon={School}
                options={['CBSE', 'ICSE', 'State Board', 'IB', 'IGCSE', 'Other']}
              />
              <EditableField
                label="Grade & Year"
                value={editedData.grade_year}
                onChange={(val) => handleFieldChange('grade_year', val)}
                fieldName="grade_year"
                icon={GraduationCap}
              />
              
              {/* Row 7: Date of Birth, City */}
              <EditableField
                label="Date of Birth"
                value={editedData.date_of_birth}
                onChange={(val) => handleFieldChange('date_of_birth', val)}
                fieldName="date_of_birth"
                icon={Cake}
                type="date"
              />
              <EditableField
                label="City"
                value={editedData.city}
                onChange={(val) => handleFieldChange('city', val)}
                fieldName="city"
                icon={MapPin}
              />
              
              {/* Row 8: Registration Date, Email (Admin only - not editable by students) */}
              <EditableField
                label="Registration Date"
                value={getCustomFieldValue('Registration Date') || editedData.registration_date}
                onChange={(val) => {
                  const field = customFieldDefinitions.find(f => f.label === 'Registration Date');
                  if (field) handleCustomFieldChange(field.id, val);
                }}
                fieldName="registration_date"
                icon={Calendar}
                type="date"
              />
              <EditableField
                label="Email (Admin View Only)"
                value={editedData.email}
                onChange={(val) => handleFieldChange('email', val)}
                fieldName="email"
                icon={Mail}
                type="email"
              />
              
              {/* Row 9: Mentor */}
              <EditableField
                label="Mentor"
                value={editedData.mentor || ''}
                onChange={(val) => handleFieldChange('mentor', val)}
                fieldName="mentor"
                icon={Users}
              />
              
              {/* DYNAMIC CUSTOM FIELDS - Inserted here one by one */}
              {customFieldDefinitions
                .filter(field => {
                  // Skip fields that are already shown in hardcoded sections
                  const hardcodedLabels = ['Father\'s Name', 'Father Name', 'Mother\'s Name', 'Mother Name', 
                    'Father\'s Mobile Number', 'Father Mobile', 'Father\'s Mobile',
                    'Mother\'s Mobile Number', 'Mother Mobile', 'Mother\'s Mobile',
                    'Father\'s Occupation', 'Father Occupation',
                    'Mother\'s Occupation', 'Mother Occupation',
                    'Fees Paid', 'Fees Remaining'];
                  return !hardcodedLabels.includes(field.label);
                })
                .map((field) => {
                  const fieldValue = editedData.custom_fields?.[field.id]?.value || '';
                  
                  return (
                    <div key={field.id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2 text-sm font-semibold text-navy">
                          <FileText className="w-4 h-4 text-primary-blue" />
                          <span>{field.label}</span>
                          {field.required && <span className="text-red-500">*</span>}
                          {field.visibility === 'admin_only' && (
                            <Badge className="ml-2 text-xs bg-red-100 text-red-700 border-red-300">
                              Admin Only
                            </Badge>
                          )}
                        </Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleFieldLock(field.id)}
                          className="h-8 w-8 p-0"
                          title={fieldLocks[field.id] ? 'Unlock field for student' : 'Lock field from student'}
                        >
                          {fieldLocks[field.id] ? 
                            <Lock className="w-4 h-4 text-red-500" /> : 
                            <Unlock className="w-4 h-4 text-green-500" />
                          }
                        </Button>
                      </div>
                      
                      {field.field_type === 'dropdown' ? (
                        <Select 
                          value={fieldValue} 
                          onValueChange={(val) => handleCustomFieldChange(field.id, val)}
                        >
                          <SelectTrigger className="h-10 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue">
                            <SelectValue placeholder={`Select ${field.label}...`} />
                          </SelectTrigger>
                          <SelectContent className="bg-blue-50 border-2 border-blue-200">
                            {field.dropdown_options?.map(option => (
                              <SelectItem key={option} value={option}>{option}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : field.field_type === 'boolean' ? (
                        <Select 
                          value={fieldValue ? 'true' : 'false'} 
                          onValueChange={(val) => handleCustomFieldChange(field.id, val === 'true')}
                        >
                          <SelectTrigger className="h-10 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-blue-50 border-2 border-blue-200">
                            <SelectItem value="true">Yes</SelectItem>
                            <SelectItem value="false">No</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'}
                          value={fieldValue}
                          onChange={(e) => {
                            console.log('Input change:', field.label, e.target.value); // Debug
                            handleCustomFieldChange(field.id, e.target.value);
                          }}
                          className="h-10 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue bg-white text-gray-900"
                          placeholder={field.description || `Enter ${field.label}...`}
                          style={{ color: '#1a1a1a' }}
                        />
                      )}
                      
                      {field.description && (
                        <p className="text-xs text-gray-500 mt-1">{field.description}</p>
                      )}
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>

        {/* Fees Information - FIXED: Shows actual values and classification */}
        <Card className="border-0 card-shadow bg-white rounded-2xl">
          <CardHeader className="border-b border-gray-100 pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-bold text-navy flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary-blue" />
                Fees Information
              </CardTitle>
              {/* Automatic Classification Badge */}
              <Badge className={`text-sm px-4 py-2 rounded-xl border-2 ${getFeesClassification().color}`}>
                {getFeesClassification().status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <EditableField
                label="Fees Paid (₹)"
                value={getFeesPaid()}
                onChange={(val) => {
                  const field = customFieldDefinitions.find(f => f.label === 'Fees Paid');
                  if (field) {
                    handleCustomFieldChange(field.id, val);
                  } else {
                    handleFieldChange('fees_paid', val);
                  }
                }}
                fieldName="fees_paid"
                icon={FileText}
                type="number"
              />
              <EditableField
                label="Fees Remaining (₹)"
                value={getFeesRemaining()}
                onChange={(val) => {
                  const field = customFieldDefinitions.find(f => f.label === 'Fees Remaining');
                  if (field) {
                    handleCustomFieldChange(field.id, val);
                  } else {
                    handleFieldChange('fees_remaining', val);
                  }
                }}
                fieldName="fees_remaining"
                icon={FileText}
                type="number"
              />
            </div>
            {/* Fees Summary */}
            <div className="mt-6 p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 font-medium">Total Fees:</span>
                <span className="text-navy font-bold text-lg">₹{(getFeesPaid() + getFeesRemaining()).toLocaleString()}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Performance Stats (Read-Only) - IMPROVED: Consistent UI */}
        <Card className="border-0 card-shadow bg-white rounded-2xl">
          <CardHeader className="border-b border-gray-100 pb-4">
            <CardTitle className="text-xl font-bold text-navy flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary-blue" />
              Performance & Learning Data
            </CardTitle>
            <p className="text-sm text-[#4D4D4D] mt-2">
              Read-only statistics and progress metrics
            </p>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl bg-blue-50 border-2 border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <Flame className="w-4 h-4 text-orange-500" />
                  <p className="text-xs font-bold text-orange-600 uppercase">GP Points</p>
                </div>
                <p className="text-2xl font-bold text-navy">{userData.xp_points || 0}</p>
              </div>
              <div className="p-4 rounded-xl bg-green-50 border-2 border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4 text-yellow-500" />
                  <p className="text-xs font-bold text-green-600 uppercase">GP Balance</p>
                </div>
                <p className="text-2xl font-bold text-navy">{userData.gp_balance || 0}</p>
              </div>
              <div className="p-4 rounded-xl bg-purple-50 border-2 border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-purple-500" />
                  <p className="text-xs font-bold text-purple-600 uppercase">Lessons Done</p>
                </div>
                <p className="text-2xl font-bold text-navy">
                  {performanceStats.lessonsCompleted}/{performanceStats.totalLessons}
                </p>
              </div>
              <div className="p-4 rounded-xl bg-pink-50 border-2 border-pink-200">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-4 h-4 text-pink-500" />
                  <p className="text-xs font-bold text-pink-600 uppercase">Avg Progress</p>
                </div>
                <p className="text-2xl font-bold text-navy">{performanceStats.overallProgress}%</p>
              </div>
            </div>

            {/* Course Progress */}
            {courseProgress.length > 0 && (
              <div className="mt-6 space-y-4">
                <h3 className="text-lg font-semibold text-navy">Course Progress</h3>
                {courseProgress.map((course) => (
                  <div key={course.courseId} className="p-4 rounded-xl bg-gray-50 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-navy">{course.courseName}</p>
                      <p className="text-sm font-semibold text-primary-blue">{course.progress.toFixed(0)}%</p>
                    </div>
                    <Progress value={course.progress} className="h-2 mb-2" />
                    <p className="text-xs text-[#4D4D4D]">
                      {course.lessonsCompleted} / {course.totalLessons} lessons completed
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Save Button - Sticky on scroll - IMPROVED: Better visibility */}
        <div className="sticky bottom-4 z-10 bg-gradient-to-r from-blue-50 to-white border-2 border-primary-blue rounded-2xl shadow-2xl p-5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-primary-blue/10 flex items-center justify-center flex-shrink-0">
                <Save className="w-5 h-5 text-primary-blue" />
              </div>
              <p className="text-sm text-[#4D4D4D]">
                <span className="font-semibold text-navy block">Save All Changes</span>
                <span className="text-xs">Profile data and field locks will be updated</span>
              </p>
            </div>
            <Button
              onClick={handleSaveProfile}
              disabled={saving}
              className="bg-primary-blue hover:bg-[#1873CC] text-white px-8 py-6 rounded-xl font-semibold transition-smooth disabled:opacity-50 whitespace-nowrap shadow-lg"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Save Profile
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
